// File: src/pages/Notices.jsx
// Description: Notices & Informations page - fetches notices from backend
// with bilingual support (EN/MM), sort by latest/earliest, and detail modal

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { API_ENDPOINTS } from '../utils/constants';
import '../styles/pages/notices.css';

// ─── API Base URL ─────────────────────────────────────────────────────────────
const BASE_URL = 'https://api.mahavihara-international.org';

// ─── Bilingual UI Labels ──────────────────────────────────────────────────────
const LABELS = {
  en: {
    pageTitle: 'Notices & Informations',
    pageSubtitle: 'Stay updated with the latest announcements',
    latest: 'Latest First',
    earliest: 'Earliest First',
    readMore: 'Read More',
    close: 'Close',
    noNotices: 'No notices found.',
    loading: 'Loading notices...',
    error: 'Failed to load notices. Please try again.',
    retry: 'Retry',
    attachments: 'Attachments',
    photos: 'Photos',
    postedOn: 'Posted on',
  },
  mm: {
    pageTitle: 'ကြေငြာချက်များ',
    pageSubtitle: 'နောက်ဆုံးကြေငြာချက်များကို ဖတ်ရှုပါ',
    latest: 'နောက်ဆုံးအရင်',
    earliest: 'အစောဆုံးအရင်',
    readMore: 'ဆက်ဖတ်ရန်',
    close: 'ပိတ်ရန်',
    noNotices: 'ကြေငြာချက်မတွေ့ပါ။',
    loading: 'ကြေငြာချက်များ ဖွင့်နေသည်...',
    error: 'ကြေငြာချက်များ ဖွင့်မရပါ။ ထပ်မံကြိုးစားပါ။',
    retry: 'ထပ်ကြိုးစားရန်',
    attachments: 'ပူးတွဲဖိုင်များ',
    photos: 'ဓာတ်ပုံများ',
    postedOn: 'တင်သည့်ရက်',
  },
};

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT = {
  LATEST: 'latest',
  EARLIEST: 'earliest',
};

// ─── Helper: format date ──────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Helper: resolve file URL from relative path ──────────────────────────────
const resolveFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/${path}`;
};

// ─── Sub-component: Notice Card ───────────────────────────────────────────────
const NoticeCard = ({ notice, lang, labels, onOpen }) => {
  const title = lang === 'mm' ? notice.title_mm : notice.title_en;
  const body = lang === 'mm' ? notice.body_mm : notice.body_en;

  // Count attachment types
  const photoCount = notice.attachments?.filter(a => a.file_type === 'photo').length || 0;
  const fileCount = notice.attachments?.filter(a => a.file_type === 'attachment').length || 0;

  return (
    <article
      className="notice-card scroll-fade hover-lift"
      onClick={() => onOpen(notice)}
    >
      {/* Header photo */}
      {notice.header_photo && (
        <div className="notice-card__image">
          <img
            src={resolveFileUrl(notice.header_photo)}
            alt={title}
            loading="lazy"
          />
        </div>
      )}

      <div className="notice-card__body">
        {/* Date */}
        <span className="notice-card__date">
          📅 {formatDate(notice.created_at)}
        </span>

        {/* Title */}
        <h3 className={`notice-card__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {title}
        </h3>

        {/* Body preview — strip HTML tags, limit to 120 chars */}
        <p className={`notice-card__preview ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {body?.replace(/<[^>]+>/g, '').slice(0, 120)}
          {body?.length > 120 ? '...' : ''}
        </p>

        {/* Attachment badges */}
        <div className="notice-card__meta">
          {photoCount > 0 && (
            <span className="notice-badge notice-badge--photo">
              📷 {photoCount} {labels.photos}
            </span>
          )}
          {fileCount > 0 && (
            <span className="notice-badge notice-badge--file">
              📎 {fileCount} {labels.attachments}
            </span>
          )}
        </div>

        <button className="btn-primary notice-card__btn">
          {labels.readMore}
        </button>
      </div>
    </article>
  );
};

// ─── Sub-component: Notice Detail Modal ──────────────────────────────────────
const NoticeModal = ({ notice, lang, labels, onClose }) => {
  if (!notice) return null;

  const title = lang === 'mm' ? notice.title_mm : notice.title_en;
  const body = lang === 'mm' ? notice.body_mm : notice.body_en;

  const photos = notice.attachments?.filter(a => a.file_type === 'photo') || [];
  const files = notice.attachments?.filter(a => a.file_type === 'attachment') || [];

  // Close modal when clicking backdrop
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="notice-modal__backdrop animate-fade-in-down"
      onClick={handleBackdrop}
    >
      <div className="notice-modal__box animate-zoom-in">
        {/* Close button */}
        <button
          className="notice-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header photo */}
        {notice.header_photo && (
          <div className="notice-modal__header-img">
            <img src={resolveFileUrl(notice.header_photo)} alt={title} />
          </div>
        )}

        <div className="notice-modal__content">
          {/* Date */}
          <span className="notice-card__date">
            {labels.postedOn}: {formatDate(notice.created_at)}
          </span>

          {/* Title */}
          <h2 className={`notice-modal__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
            {title}
          </h2>

          {/* Body — rendered as HTML from backend */}
          <div
            className={`notice-modal__body ${lang === 'mm' ? 'font-myanmar' : ''}`}
            dangerouslySetInnerHTML={{ __html: body }}
          />

          {/* Photo attachments grid */}
          {photos.length > 0 && (
            <div className="notice-modal__section">
              <h4 className="notice-modal__section-title">
                📷 {labels.photos}
              </h4>
              <div className="notice-modal__photos">
                {photos.map(p => (
                  <a
                    key={p.id}
                    href={resolveFileUrl(p.file_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={resolveFileUrl(p.file_path)}
                      alt="photo attachment"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* File attachments (PDFs etc.) */}
          {files.length > 0 && (
            <div className="notice-modal__section">
              <h4 className="notice-modal__section-title">
                📎 {labels.attachments}
              </h4>
              <ul className="notice-modal__files">
                {files.map(f => {
                  const fileName = f.file_path.split('/').pop();
                  return (
                    <li key={f.id}>
                      <a
                        href={resolveFileUrl(f.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="notice-file-link"
                      >
                        📄 {fileName}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page Component ──────────────────────────────────────────────────────
const Notices = () => {
  // Use currentLanguage from context (values: 'en' | 'my')
  const { currentLanguage } = useLanguage();

  // Map context language code to local label key
  const lang = currentLanguage === 'my' ? 'mm' : 'en';
  const labels = LABELS[lang];

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(SORT.LATEST); // default: latest first
  const [selectedNotice, setSelectedNotice] = useState(null);

  // ── Fetch all notices from backend ──
  const fetchNotices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/notices`);
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      // Backend returns { success, message, data: [...] }
      setNotices(data.data || data);
    } catch (err) {
      setError(labels.error);
    } finally {
      setLoading(false);
    }
  }, [labels.error]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // ── Scroll fade intersection observer ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach(e =>
          e.target.classList.toggle('visible', e.isIntersecting)
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.scroll-fade').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [notices]);

  // ── Sort notices by created_at ──
  const sorted = [...notices].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sort === SORT.LATEST ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className="notices-page">

      {/* ── Page Banner ── */}
      <div className="page-banner">
        <div className="container">
          <div className="banner-content">
            <h1 className={`banner-title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageTitle}
            </h1>
            <p className={`banner-subtitle ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sort Controls ── */}
      <div className="notices-controls container">
        <div className="notices-sort">
          {/* Latest button */}
          <button
            className={`notices-sort-btn ${sort === SORT.LATEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.LATEST)}
          >
            🕐 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.latest}</span>
          </button>

          {/* Earliest button */}
          <button
            className={`notices-sort-btn ${sort === SORT.EARLIEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.EARLIEST)}
          >
            🕰 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.earliest}</span>
          </button>
        </div>
      </div>

      {/* ── Notices Grid ── */}
      <section className="notices-grid-section section-padding">
        <div className="container">

          {/* Loading state */}
          {loading && (
            <div className="notices-state text-center">
              <div className="spinner-ring" />
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.loading}</p>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="notices-state notices-state--error text-center">
              <p>{error}</p>
              <button className="btn-primary" onClick={fetchNotices}>
                {labels.retry}
              </button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && sorted.length === 0 && (
            <div className="notices-state text-center">
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.noNotices}</p>
            </div>
          )}

          {/* Notices grid */}
          {!loading && !error && sorted.length > 0 && (
            <div className="notices-grid">
              {sorted.map(notice => (
                <NoticeCard
                  key={notice.id}
                  notice={notice}
                  lang={lang}
                  labels={labels}
                  onOpen={setSelectedNotice}
                />
              ))}
            </div>
          )}

        </div>
      </section>

      {/* ── Detail Modal ── */}
      {selectedNotice && (
        <NoticeModal
          notice={selectedNotice}
          lang={lang}
          labels={labels}
          onClose={() => setSelectedNotice(null)}
        />
      )}

    </div>
  );
};

export default Notices;